import { useWatch } from "react-hook-form";
import { useIntl } from "react-intl";

import type { HotelSearchFormInputValues } from "@/features/hotels/utils/hotelsValidation";
import { DatePicker } from "@/features/search/components/DatePicker/DatePicker";
import {
  HotelSearchTrackingEvents,
  rudderAnalytics,
  type SearchSource,
} from "@/lib/rudderstack";
import { getDateString } from "@/utils/dates";

import type { HotelSearchForm } from "../HotelSearchForm";

type HotelDatePickerProps = {
  form: HotelSearchForm;
  analyticsSource: SearchSource;
  isCompact: boolean;
  onChange?: (values: HotelSearchFormInputValues) => void;
};

export function HotelDatePicker({
  form,
  analyticsSource,
  isCompact,
  onChange,
}: HotelDatePickerProps) {
  const intl = useIntl();

  const [checkIn, checkOut] = useWatch({
    control: form.control,
    name: ["dates.checkIn", "dates.checkOut"],
  });

  return (
    <DatePicker
      compact={isCompact}
      label={intl.formatMessage({
        defaultMessage: "Travel dates",
        description: "hotels.search.checkInAndCheckOut",
      })}
      calendarProps={{
        selectionInfoVariant: "text",
      }}
      onChange={(values) => {
        rudderAnalytics.track(
          HotelSearchTrackingEvents.USER_CHANGED_HOTEL_CALENDAR_DATE,
          {
            source: analyticsSource,
            dateFrom: getDateString(values.from!),
            dateTo: getDateString(values.to!),
          },
        );

        form.reset({
          ...form.getValues(),
          dates: {
            checkIn: values.from ?? undefined,
            checkOut: values.to ?? undefined,
          },
        });

        onChange?.(form.getValues());
      }}
      value={{
        from: checkIn,
        to: checkOut,
      }}
      isRange
      onFieldClick={() => {
        rudderAnalytics.track(
          HotelSearchTrackingEvents.USER_CLICKED_HOTEL_CALENDAR_INPUT,
          {
            source: analyticsSource,
          },
        );
      }}
    />
  );
}
